import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import carSvgUrl from "../assets/easter/car.svg";
import fuelStationSvgUrl from "../assets/easter/fuel-station.svg";
import pumpHandleSvgUrl from "../assets/easter/pump-handle.svg";

/**
 * Fuel-pump easter egg.
 *
 * Replaces the FAB on the overview page with a small gas station. The user
 * grabs the pump (touch/mouse), a car drives in from the left edge of
 * the viewport, and dropping the nozzle on the car's fuel door opens the
 * "new entry" modal. While the modal is open the car stays parked with
 * the nozzle in the tank — like a real fill-up. Once the modal closes
 * (saved or canceled), the nozzle retracts and the car drives off.
 *
 * Phase machine:
 *   idle     — nothing visible but the docked station.
 *   active   — pump is in the user's hand, car is driving in / parked.
 *   fueling  — nozzle is in the fuel door, modal is open. Car is frozen.
 *   leaving  — nozzle has returned to the dock, car is driving off-screen.
 *              The pump is grabbable again immediately during this phase
 *              so the user is never blocked waiting for the animation.
 *
 * `tripId` keys the rendered car so each new grab spawns a fresh car
 * driving in from off-screen left, even if a previous trip's car is still
 * mid-leave.
 */

interface FuelPumpEasterEggProps {
  /** Called when the user successfully drops the nozzle on the fuel door. */
  onDeliver: () => void;
  /** Whether the new-entry modal is currently open. The easter egg keeps
   *  the car parked + nozzle docked in the door while this is true. */
  modalOpen: boolean;
}

interface Droplet {
  el: HTMLDivElement;
  x: number;
  y: number;
  vx: number;
  vy: number;
  born: number;
}

const GRAVITY = 1100; // px/s²
const DROPLET_TTL_MS = 1400;
const DROPLET_RATE_HZ = 28;
const LEAVE_DURATION_MS = 1100;
const DESKTOP_BREAKPOINT = 900;
const CAR_WIDTH = 367;
const MOBILE_CAR_STATION_GAP = 12;
const MOBILE_STATION_WIDTH = 100;
const MOBILE_STATION_RIGHT = 16;

// --- Geometry constants -------------------------------------------------
//
// All coords are top-left-origin pixel offsets within the relevant SVG /
// element. The handle div is 80x96 (the touch target); the visible SVG is
// narrower than that so finger taps are forgiving.

// Where, on the docked station element, the hose attaches to the body.
const STATION_HOSE_OUT_X = 32;
const STATION_HOSE_OUT_Y = 78;

// Hose-in point on the docked, unmirrored handle, and on the in-hand
// mirrored handle. These are relative to the 80x96 handle div, not to the
// SVG's own viewBox.
const HANDLE_HOSE_IN_DOCKED_X = 13;
const HANDLE_HOSE_IN_ACTIVE_X = 25;
const HANDLE_HOSE_IN_Y = 89;
const HANDLE_NOZZLE_X = 16;
const HANDLE_NOZZLE_Y = 30;

// Where the docked handle sits inside the station element.
const HANDLE_DOCK_LEFT = -2;
const HANDLE_DOCK_TOP = -2;

export default function FuelPumpEasterEgg({
  onDeliver,
  modalOpen,
}: FuelPumpEasterEggProps) {
  type Phase = "idle" | "active" | "fueling" | "leaving";
  const [phase, setPhase] = useState<Phase>("idle");
  const [tripId, setTripId] = useState(0);
  const [overDoor, setOverDoor] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === "undefined"
      ? false
      : window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches,
  );

  // Pointer-driven handle position (top-left of handle div, in viewport
  // coords). Mirrors into a ref so the rAF loop / async pointer handlers
  // never see stale values.
  const [handlePos, setHandlePos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const handlePosRef = useRef<{ x: number; y: number } | null>(null);

  const stationRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const fuelDoorRef = useRef<HTMLDivElement | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const grabOffsetRef = useRef<{ x: number; y: number }>({
    x: HANDLE_HOSE_IN_ACTIVE_X,
    y: HANDLE_HOSE_IN_Y,
  });
  const dropletsRef = useRef<Droplet[]>([]);
  const lastSpawnRef = useRef<number>(0);
  const lastFrameRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);
  const leaveTimerRef = useRef<number | null>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const phaseRef = useRef<Phase>("idle");
  // Tracks "saw modal open during fueling" so we don't trip on the
  // initial render (modal is still false for one frame after delivery).
  const modalSeenOpenRef = useRef(false);

  useLayoutEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // ------------------------------------------------------------------
  // rAF droplet loop
  // ------------------------------------------------------------------

  const tick = useCallback((now: number) => {
    const overlay = overlayRef.current;
    if (!overlay) {
      rafRef.current = null;
      return;
    }
    const last = lastFrameRef.current || now;
    const dt = Math.min(50, now - last) / 1000;
    lastFrameRef.current = now;

    // Spawn droplets only while pumping (active drag, not docked, not
    // inserted in the tank).
    const handle = handlePosRef.current;
    if (
      phaseRef.current === "active" &&
      handle &&
      now - lastSpawnRef.current > 1000 / DROPLET_RATE_HZ
    ) {
      lastSpawnRef.current = now;
      const nozzleX = handle.x + HANDLE_NOZZLE_X;
      const nozzleY = handle.y + HANDLE_NOZZLE_Y;
      // 2-3 droplets per spawn for a fountain-y burst.
      const burst = 2 + (Math.random() < 0.4 ? 1 : 0);
      for (let i = 0; i < burst; i++) {
        const el = document.createElement("div");
        el.className = "gas-droplet";
        overlay.appendChild(el);
        // Mostly downward, slight forward/backward variance.
        const speed = 70 + Math.random() * 70;
        const angle = (Math.random() - 0.5) * 0.9; // ~±26°
        dropletsRef.current.push({
          el,
          x: nozzleX,
          y: nozzleY,
          vx: Math.sin(angle) * speed - 30, // bias slightly left (toward car)
          vy: -Math.cos(angle) * 30 + 40, // small upward kick + downward bias
          born: now,
        });
      }
    }

    const survivors: Droplet[] = [];
    for (const d of dropletsRef.current) {
      const age = now - d.born;
      if (age > DROPLET_TTL_MS) {
        d.el.remove();
        continue;
      }
      d.vy += GRAVITY * dt;
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      const fade = 1 - age / DROPLET_TTL_MS;
      d.el.style.transform = `translate3d(${d.x}px, ${d.y}px, 0)`;
      d.el.style.opacity = String(0.85 * fade);
      if (d.y > window.innerHeight + 40) {
        d.el.remove();
        continue;
      }
      survivors.push(d);
    }
    dropletsRef.current = survivors;

    // Keep running while we still have particles or are actively pumping.
    if (phaseRef.current === "active" || dropletsRef.current.length > 0) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      rafRef.current = null;
    }
  }, []);

  const startRaf = useCallback(() => {
    if (rafRef.current !== null) return;
    lastFrameRef.current = 0;
    lastSpawnRef.current = 0;
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const cleanupDragListeners = useCallback(() => {
    if (dragCleanupRef.current) {
      dragCleanupRef.current();
      dragCleanupRef.current = null;
    }
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      cleanupDragListeners();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (leaveTimerRef.current !== null) {
        clearTimeout(leaveTimerRef.current);
      }
      for (const d of dropletsRef.current) d.el.remove();
      dropletsRef.current = [];
    };
  }, [cleanupDragListeners]);

  // ------------------------------------------------------------------
  // Hit testing
  // ------------------------------------------------------------------

  const updateOverDoor = useCallback((clientX: number, clientY: number) => {
    const door = fuelDoorRef.current;
    if (!door) {
      setOverDoor(false);
      return false;
    }
    const r = door.getBoundingClientRect();
    const inside =
      clientX >= r.left &&
      clientX <= r.right &&
      clientY >= r.top &&
      clientY <= r.bottom;
    setOverDoor(inside);
    return inside;
  }, []);

  // ------------------------------------------------------------------
  // Pointer handling
  //
  // Only the *down* event is bound to the pump-handle DOM node — once the
  // user starts a drag we synchronously attach pointermove/up/cancel to
  // `window`. This avoids a class of mobile bugs where pointer-capture,
  // delayed effects, or hidden source elements caused the up event to be
  // lost (the pump would get "stuck" when the user tapped and released
  // the station without moving).
  // ------------------------------------------------------------------

  const beginLeaving = useCallback(() => {
    setHandlePos(null);
    handlePosRef.current = null;
    phaseRef.current = "leaving";
    setPhase("leaving");
    if (leaveTimerRef.current !== null) {
      clearTimeout(leaveTimerRef.current);
    }
    leaveTimerRef.current = window.setTimeout(() => {
      leaveTimerRef.current = null;
      if (phaseRef.current === "leaving") {
        phaseRef.current = "idle";
        setPhase("idle");
      }
    }, LEAVE_DURATION_MS);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Allow re-grab from idle OR leaving (cancel the in-flight retreat).
      if (phaseRef.current === "active" || phaseRef.current === "fueling") {
        return;
      }
      e.preventDefault();
      cleanupDragListeners();
      if (leaveTimerRef.current !== null) {
        clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = null;
      }
      pointerIdRef.current = e.pointerId;
      const rect = e.currentTarget.getBoundingClientRect();
      grabOffsetRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
      const pos = {
        x: rect.left,
        y: rect.top,
      };
      handlePosRef.current = pos;
      setHandlePos(pos);
      // Fresh trip: remount the car so it drives in from off-screen left
      // even if the previous car is still leaving.
      setTripId((t) => t + 1);
      modalSeenOpenRef.current = false;
      phaseRef.current = "active";
      setPhase("active");
      startRaf();

      const onMove = (event: PointerEvent) => {
        if (pointerIdRef.current !== event.pointerId) return;
        event.preventDefault();
        const next = {
          x: event.clientX - grabOffsetRef.current.x,
          y: event.clientY - grabOffsetRef.current.y,
        };
        handlePosRef.current = next;
        setHandlePos(next);
        updateOverDoor(
          next.x + HANDLE_NOZZLE_X,
          next.y + HANDLE_NOZZLE_Y,
        );
      };

      const onEnd = (event: PointerEvent) => {
        if (pointerIdRef.current !== event.pointerId) return;
        event.preventDefault();
        cleanupDragListeners();
        pointerIdRef.current = null;
        const next = {
          x: event.clientX - grabOffsetRef.current.x,
          y: event.clientY - grabOffsetRef.current.y,
        };
        handlePosRef.current = next;
        setHandlePos(next);
        const delivered = updateOverDoor(
          next.x + HANDLE_NOZZLE_X,
          next.y + HANDLE_NOZZLE_Y,
        );
        setOverDoor(false);
        if (delivered) {
          // Snap the handle so its nozzle sits at the fuel-door center,
          // then freeze in fueling state. The car/pump stay there until
          // the modal closes.
          const door = fuelDoorRef.current?.getBoundingClientRect();
          if (door) {
            const cx = door.left + door.width / 2;
            const cy = door.top + door.height / 2;
            const snap = {
              x: cx - HANDLE_NOZZLE_X,
              y: cy - HANDLE_NOZZLE_Y,
            };
            handlePosRef.current = snap;
            setHandlePos(snap);
          }
          modalSeenOpenRef.current = false;
          phaseRef.current = "fueling";
          setPhase("fueling");
          onDeliver();
        } else {
          beginLeaving();
        }
      };

      window.addEventListener("pointermove", onMove, true);
      window.addEventListener("pointerup", onEnd, true);
      window.addEventListener("pointercancel", onEnd, true);
      dragCleanupRef.current = () => {
        window.removeEventListener("pointermove", onMove, true);
        window.removeEventListener("pointerup", onEnd, true);
        window.removeEventListener("pointercancel", onEnd, true);
      };
    },
    [
      beginLeaving,
      cleanupDragListeners,
      onDeliver,
      startRaf,
      updateOverDoor,
    ],
  );

  // ------------------------------------------------------------------
  // Modal close → leave
  // ------------------------------------------------------------------

  useEffect(() => {
    if (phase !== "fueling") return;
    if (modalOpen) {
      modalSeenOpenRef.current = true;
    } else if (modalSeenOpenRef.current) {
      modalSeenOpenRef.current = false;
      beginLeaving();
    }
  }, [modalOpen, phase, beginLeaving]);

  // ------------------------------------------------------------------
  // Hose path geometry
  // ------------------------------------------------------------------

  const stationRect = useStationRect(stationRef, phase);

  const hoseStart = stationRect
    ? {
        x: stationRect.left + STATION_HOSE_OUT_X,
        y: stationRect.top + STATION_HOSE_OUT_Y,
      }
    : null;

  let hoseEnd: { x: number; y: number } | null = null;
  if (handlePos) {
    hoseEnd = {
      x: handlePos.x + HANDLE_HOSE_IN_ACTIVE_X,
      y: handlePos.y + HANDLE_HOSE_IN_Y,
    };
  } else if (stationRect) {
    hoseEnd = {
      x: stationRect.left + HANDLE_DOCK_LEFT + HANDLE_HOSE_IN_DOCKED_X,
      y: stationRect.top + HANDLE_DOCK_TOP + HANDLE_HOSE_IN_Y,
    };
  }

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  const showCar = phase !== "idle";
  const showHose = true;
  // Whether the docked handle is currently "in hand" / inserted, in which
  // case the dock copy is hidden and the overlay copy is the live one.
  const handleAway = phase === "active" || phase === "fueling";

  const layer = (
    <>
      <div ref={stationRef} className="gas-station" aria-hidden="true">
        <StationSvg />
        <div
          className="gas-station__pump-handle"
          style={
            handleAway
              ? { visibility: "hidden", left: HANDLE_DOCK_LEFT, top: HANDLE_DOCK_TOP }
              : {
                  left: HANDLE_DOCK_LEFT,
                  top: HANDLE_DOCK_TOP,
                  transition:
                    phase === "leaving"
                      ? "left 600ms cubic-bezier(.2,.7,.3,1), top 600ms cubic-bezier(.2,.7,.3,1)"
                      : undefined,
                }
          }
          onPointerDown={onPointerDown}
          role="button"
          aria-label="Drag the gas pump to the car to log fuel"
        >
          <PumpHandleSvg />
        </div>
      </div>

      <div ref={overlayRef} className="gas-station__overlay">
        {showCar ? (
          <Car
            key={tripId}
            parked={phase === "active" || phase === "fueling"}
            isDesktop={isDesktop}
            stationLeft={stationRect?.left ?? null}
            fuelDoorRef={fuelDoorRef}
            highlightDoor={overDoor || phase === "fueling"}
          />
        ) : null}

        {showHose && hoseStart && hoseEnd ? (
          <svg
            className="gas-station__hose"
            width="100%"
            height="100%"
          >
            <path
              d={hosePath(hoseStart, hoseEnd)}
              fill="none"
              stroke="#1a1d26"
              strokeWidth={5}
              strokeLinecap="round"
            />
            <path
              d={hosePath(hoseStart, hoseEnd)}
              fill="none"
              stroke="#3a3f4f"
              strokeWidth={2}
              strokeLinecap="round"
            />
          </svg>
        ) : null}
      </div>

      {handleAway && handlePos ? (
        <div
          className="gas-station__pump-handle gas-station__pump-handle--in-hand"
          style={{
            position: "fixed",
            left: handlePos.x,
            top: handlePos.y,
            pointerEvents: phase === "active" ? "auto" : "none",
            cursor: phase === "active" ? "grabbing" : "default",
          }}
        >
          <PumpHandleSvg />
        </div>
      ) : null}
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(layer, document.body);
}

// ----------------------------------------------------------------------
// Car (own component so it can re-mount with key=tripId and animate
// drive-in from off-screen left)
// ----------------------------------------------------------------------

interface CarProps {
  parked: boolean;
  isDesktop: boolean;
  stationLeft: number | null;
  fuelDoorRef: React.MutableRefObject<HTMLDivElement | null>;
  highlightDoor: boolean;
}

function Car({
  parked,
  isDesktop,
  stationLeft,
  fuelDoorRef,
  highlightDoor,
}: CarProps) {
  type Stage = "enter-init" | "parked" | "leaving";
  const [stage, setStage] = useState<Stage>("enter-init");

  // After mount, on the *second* frame, flip from off-screen-left to
  // parked so the transition kicks in.
  useEffect(() => {
    let canceled = false;
    const id = requestAnimationFrame(() => {
      const id2 = requestAnimationFrame(() => {
        if (!canceled) setStage("parked");
      });
      // store id2 on window var for cleanup; tiny so we just leak it
      void id2;
    });
    return () => {
      canceled = true;
      cancelAnimationFrame(id);
    };
  }, []);

  // When parked goes false we drive off.
  useEffect(() => {
    if (!parked && stage === "parked") setStage("leaving");
  }, [parked, stage]);

  let transform: string;
  let transition: string;
  if (stage === "enter-init") {
    transform = `translateX(-${CAR_WIDTH + 120}px)`;
    transition = "none";
  } else if (stage === "parked") {
    // Desktop keeps the car in the page body. Mobile parks the car with a
    // fixed body gap to the station, even on narrow screens where the car
    // needs to sit partly off the left edge.
    if (isDesktop) {
      transform = `translateX(calc(60vw - ${CAR_WIDTH / 2}px))`;
    } else if (stationLeft !== null) {
      transform = `translateX(${Math.round(
        stationLeft - CAR_WIDTH - MOBILE_CAR_STATION_GAP,
      )}px)`;
    } else {
      const fallbackOffset =
        MOBILE_STATION_RIGHT +
        MOBILE_STATION_WIDTH +
        CAR_WIDTH +
        MOBILE_CAR_STATION_GAP;
      transform = `translateX(calc(100vw - ${fallbackOffset}px - env(safe-area-inset-right)))`;
    }
    transition = "transform 1200ms cubic-bezier(.4,.1,.2,1)";
  } else {
    transform = "translateX(115vw)";
    transition = "transform 900ms cubic-bezier(.55,.05,.7,.4)";
  }

  return (
    <div
      className={"easter-car" + (highlightDoor ? " easter-car--target" : "")}
      style={{ left: 0, transform, transition }}
    >
      <CarSvg />
      <div ref={fuelDoorRef} className="easter-car__fuel-door-hit" />
    </div>
  );
}

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

function hosePath(
  a: { x: number; y: number },
  b: { x: number; y: number },
): string {
  const dx = b.x - a.x;
  const droop = Math.min(180, 50 + Math.abs(dx) * 0.22);
  const c1x = a.x + dx * 0.25;
  const c1y = a.y + droop;
  const c2x = a.x + dx * 0.75;
  const c2y = b.y + droop * 0.5;
  return `M ${a.x} ${a.y} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${b.x} ${b.y}`;
}

function useStationRect(
  ref: React.MutableRefObject<HTMLDivElement | null>,
  phase: string,
): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    let canceled = false;
    const measure = () => {
      if (canceled) return;
      const el = ref.current;
      if (el) setRect(el.getBoundingClientRect());
    };

    // Measure only on mount/resize. iOS can report transient fixed-element
    // rects during scroll, which makes the hose appear to scroll even though
    // the station itself is fixed.
    measure();
    const raf1 = requestAnimationFrame(measure);
    const raf2 = requestAnimationFrame(() => {
      requestAnimationFrame(measure);
    });
    const settleTimer = window.setTimeout(measure, 260);
    const lateSettleTimer = window.setTimeout(measure, 520);

    const el = ref.current;
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    if (el && resizeObserver) {
      resizeObserver.observe(el);
    }

    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    return () => {
      canceled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(settleTimer);
      window.clearTimeout(lateSettleTimer);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, [ref, phase]);
  return rect;
}

// ----------------------------------------------------------------------
// Editable SVG asset wrappers
// ----------------------------------------------------------------------

function StationSvg() {
  return (
    <img
      alt=""
      className="gas-station__svg"
      draggable={false}
      src={fuelStationSvgUrl}
    />
  );
}

function PumpHandleSvg() {
  return (
    <img
      alt=""
      className="gas-station__pump-handle-svg"
      draggable={false}
      src={pumpHandleSvgUrl}
    />
  );
}

function CarSvg() {
  return (
    <img
      alt=""
      className="easter-car__svg"
      draggable={false}
      src={carSvgUrl}
    />
  );
}
